import { useCallback, useRef, useState } from "react";

// The bulk-scan drop area that opens above the drafts list. Presentational —
// the queue itself lives in useBulkScan.
export function BulkDropZone({
  onFiles,
  onStop,
  onResume,
  running,
  paused,
  queued,
  scanning,
}: {
  onFiles: (files: FileList | null) => void;
  onStop: () => void;
  onResume: () => void;
  running: boolean;
  // Stopped mid-batch: the queue is parked until Resume or the next drop.
  paused: boolean;
  queued: number;
  scanning: number;
}) {
  const [dragging, setDragging] = useState(false);
  // Dragging over a child fires dragleave on the parent, so track enter/leave
  // depth instead of clearing the highlight on the first leave.
  const dragDepth = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      // Without this the browser navigates to the dropped file.
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      onFiles(e.dataTransfer.files);
    },
    [onFiles],
  );

  return (
    <div className="mb-5">
      {/* Click to browse or drop files anywhere on the box. */}
      <label
        onDragEnter={handleDragEnter}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`flex min-h-[120px] cursor-pointer flex-col items-center justify-center rounded-row border-2 border-dashed transition-colors ${
          dragging ? "border-accent bg-surface-strong" : "border-line bg-surface hover:border-accent"
        }`}
      >
        <input
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            onFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <span className={`text-sm ${dragging ? "text-accent" : "text-muted"}`}>
          {dragging ? "Drop to add" : "Drop card images here, or choose files"}
        </span>
        <span className="mt-1 text-xs text-faint">
          Scanning starts as soon as you add files; name them <code>card_A</code> / <code>card_B</code> to pair front
          and back
        </span>
      </label>

      {(running || (paused && queued > 0)) && (
        <div className="mt-3 flex items-center gap-3">
          <span className="text-sm text-muted">
            {running ? `Scanning ${scanning}…${queued > 0 ? ` · ${queued} queued` : ""}` : `Paused — ${queued} waiting`}
          </span>
          {running ? (
            <button type="button" onClick={onStop} className="btn-secondary btn-sm">
              Stop
            </button>
          ) : (
            <button type="button" onClick={onResume} className="btn-primary btn-sm">
              Resume
            </button>
          )}
        </div>
      )}
    </div>
  );
}
